import { useEffect, useRef, useState } from "react";
import { MapPin, Search, Users, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const fmt = (n) => Number(n || 0).toLocaleString("es-MX");

/** Buscador de poblaciones de México (2,469 municipios). onChange({key,name,state,label}, audience) */
export default function CityPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef();

  useEffect(() => {
    if (value) return;
    (async () => {
      try {
        const { data } = await api.get("/merchants/me/city");
        if (data.city) { onChange(data.city); setAudience(data.audience); }
      } catch (e) { console.warn("silent error:", e && e.message || e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!value?.key) { setAudience(null); return; }
    setLoading(true);
    api.get(`/merchants/me/cities/${value.key}/audience`).then(({ data }) => setAudience(data)).catch(() => setAudience(null)).finally(() => setLoading(false));
  }, [value?.key]);

  const search = (text) => {
    setQ(text);
    clearTimeout(timer.current);
    if (text.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/public/cities?q=${encodeURIComponent(text)}`);
        setResults(data.cities || []); setOpen(true);
      } catch (e) { console.warn("silent error:", e && e.message || e); }
    }, 220);
  };

  const pick = (c) => { onChange(c); setQ(""); setResults([]); setOpen(false); };

  return (
    <div className="rounded-2xl border-2 border-[#FF007A]/40 bg-[#FF007A]/[0.03] p-3 space-y-2" data-testid="city-picker">
      <div className="text-xs font-bold text-[#FF007A] flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Población destino (todo México)</div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-3.5 text-neutral-400" />
        <Input value={q} onChange={(e) => search(e.target.value)} onFocus={() => results.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Escribe la población… ej. Guadalajara, Jalisco" className="pl-9 h-11 rounded-xl bg-white" data-testid="city-search-input" />
        {open && results.length > 0 && (
          <ul className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-neutral-200 bg-white shadow-xl" data-testid="city-results">
            {results.map((c) => (
              <li key={c.key}>
                <button type="button" onMouseDown={() => pick(c)} className="w-full text-left px-3 py-2 hover:bg-neutral-50 flex items-center justify-between gap-2" data-testid={`city-option-${c.key}`}>
                  <span className="text-sm"><strong>{c.name}</strong>, <span className="text-neutral-500">{c.state}</span></span>
                  <span className="text-[10px] text-neutral-400 font-mono">{fmt(c.population)} hab.</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {value ? (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-white border border-neutral-200 px-3 py-2" data-testid="city-selected">
          <div className="min-w-0">
            <div className="font-heading font-extrabold text-sm truncate">{value.name}, {value.state}</div>
            <div className="text-[11px] text-neutral-500 flex items-center gap-1">
              <Users className="w-3 h-3" />
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : audience ? (
                <span data-testid="city-audience"><strong className="text-[#0055FF]">{fmt(audience.users_total)}</strong> usuarios HOLA RADAR en esta población · {fmt(audience.new_reach)} fuera de tu base</span>
              ) : "—"}
            </div>
          </div>
          <button type="button" onClick={() => onChange(null)} className="text-neutral-400 hover:text-neutral-700" aria-label="Quitar población" data-testid="city-clear"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <p className="text-[11px] text-neutral-500">El cupón le llegará a <strong>todos los usuarios de HOLA RADAR de esa población</strong> (registrados al escanear cualquier QR de la plataforma), aunque no estén en tu base de datos personal. Máx. 4 campañas de población al mes.</p>
      )}
    </div>
  );
}
